'use client'

import { useState, useEffect, MouseEvent, useCallback, ReactComponentElement } from 'react'
import { Button, Card, Cell, Divider, Spacer } from '@vinted/web-ui'

import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'

import { DropdownActionsType } from '../../types'

type Props = {
  actions: Array<DropdownActionsType>
  buttonProps: React.ComponentProps<typeof Button>
  separator?: ReactComponentElement<typeof Divider | typeof Spacer>
  styling?: (typeof Cell.Styling)[keyof typeof Cell.Styling]
  onDropdownOpen?: () => void
}

const DropdownMenu = ({ actions, buttonProps, styling, separator, onDropdownOpen }: Props) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)

  const handleDocumentClick = useCallback(() => {
    setIsDropdownVisible(false)
  }, [])

  useEffect(() => {
    if (isDropdownVisible) {
      document.addEventListener('click', handleDocumentClick)
    }

    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [isDropdownVisible, handleDocumentClick])

  const handleButtonClick = (event: MouseEvent) => {
    event.stopPropagation()

    if (!isDropdownVisible) onDropdownOpen?.()

    setIsDropdownVisible(prevIsDropdownVisible => !prevIsDropdownVisible)
  }

  const renderActions = () =>
    actions.map(action => {
      const { id, text, url, prefix, onClick } = action

      return (
        <Cell
          prefix={prefix}
          testId={`${id}-button`}
          key={id}
          body={text}
          styling={styling || 'narrow'}
          type="navigating"
          url={url}
          onClick={onClick}
        />
      )
    })

  const renderSeparatedList = () => (
    <SeparatedList separator={separator}>{renderActions()}</SeparatedList>
  )

  return (
    <>
      <Button {...buttonProps} onClick={handleButtonClick} />
      {isDropdownVisible && (
        <div className="dropdown-menu">
          <Card styling="lifted">{separator ? renderSeparatedList() : renderActions()}</Card>
        </div>
      )}
    </>
  )
}

export default DropdownMenu
